import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostListener,
    Input,
    Output,
} from '@angular/core';

export interface MonthRange {
    dateFrom: Date | null;
    dateTo: Date | null;
}

interface YearMonth {
    year: number;
    month: number;
}

@Component({
    selector: 'month-range-picker',
    templateUrl: './month-range-picker.component.html',
    styleUrls: ['./month-range-picker.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MonthRangePickerComponent {
    @Input() dateFrom: Date | null = null;
    @Input() dateTo: Date | null = null;

    /** Optional lower bound. Months before minDate are disabled. */
    @Input() minDate: Date | null = null;

    /** Optional upper bound. Months after maxDate are disabled and dateTo is
     *  clamped to maxDate on commit. */
    @Input() maxDate: Date | null = null;

    /** What "Reset" snaps dateFrom to. If null, Reset clears the range. */
    @Input() defaultFrom: Date | null = null;

    /** What "Reset" snaps dateTo to. If null, Reset clears the range. */
    @Input() defaultTo: Date | null = null;

    @Input() placeholder = 'All';

    /** Single-month mode: one click selects that month (dateFrom = dateTo) and
     *  closes, instead of the two-click range selection. Default is range mode,
     *  so existing range consumers are unaffected. */
    @Input() single = false;

    @Output() rangeChange = new EventEmitter<MonthRange>();

    readonly monthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    showPicker = false;
    viewYear: number = new Date().getFullYear();
    pendingStart: YearMonth | null = null;
    hoverMonth: YearMonth | null = null;

    constructor(private cd: ChangeDetectorRef) {}

    // ─── Computed ────────────────────────────────────────────────────────────

    get label(): string {
        const from = this.dateFrom ? this.formatMonth(this.dateFrom) : '';
        const to = this.dateTo ? this.formatMonth(this.dateTo) : '';
        if (!from && !to) return '';
        if (from && to) return from === to ? from : `${from} — ${to}`;
        return from ? `From ${from}` : `To ${to}`;
    }

    get canGoPrevYear(): boolean {
        return !this.minDate || this.viewYear > this.minDate.getFullYear();
    }

    get canGoNextYear(): boolean {
        return !this.maxDate || this.viewYear < this.maxDate.getFullYear();
    }

    /** True when the current range already matches the defaults (month-level
     *  comparison). Used to hide the Reset button when there's nothing to do. */
    get isAtDefault(): boolean {
        return this.isSameMonth(this.dateFrom, this.defaultFrom)
            && this.isSameMonth(this.dateTo, this.defaultTo);
    }

    private isSameMonth(a: Date | null, b: Date | null): boolean {
        if (!a && !b) return true;
        if (!a || !b) return false;
        const da = a instanceof Date ? a : new Date(a);
        const db = b instanceof Date ? b : new Date(b);
        return da.getFullYear() === db.getFullYear() && da.getMonth() === db.getMonth();
    }

    // ─── User actions ────────────────────────────────────────────────────────

    toggle(event: MouseEvent): void {
        event.stopPropagation();
        this.showPicker = !this.showPicker;
        if (this.showPicker) {
            const ref = this.dateFrom ? new Date(this.dateFrom) : new Date();
            this.viewYear = ref.getFullYear();
            this.pendingStart = null;
            this.hoverMonth = null;
        }
        this.cd.markForCheck();
    }

    prevYear(): void {
        if (this.canGoPrevYear) this.viewYear--;
    }

    nextYear(): void {
        if (this.canGoNextYear) this.viewYear++;
    }

    onMonthClick(year: number, month: number): void {
        if (this.isMonthDisabled(year, month)) return;

        if (this.single) {
            // One click picks the month and closes (dateFrom = dateTo = month).
            const d = new Date(year, month, 1, 0, 0, 0, 0);
            this.pendingStart = null;
            this.hoverMonth = null;
            this.showPicker = false;
            this.rangeChange.emit({ dateFrom: d, dateTo: d });
            this.cd.markForCheck();
            return;
        }

        if (!this.pendingStart) {
            this.pendingStart = { year, month };
            this.hoverMonth = null;
            return;
        }

        let start = this.pendingStart;
        let end: YearMonth = { year, month };
        if (this.compareYM(end, start) < 0) {
            const t = start; start = end; end = t;
        }

        const newFrom = new Date(start.year, start.month, 1, 0, 0, 0, 0);
        let newTo = new Date(end.year, end.month + 1, 0, 23, 59, 59, 999);
        if (this.maxDate && newTo > this.maxDate) newTo = this.maxDate;

        this.pendingStart = null;
        this.hoverMonth = null;
        this.showPicker = false;
        this.rangeChange.emit({ dateFrom: newFrom, dateTo: newTo });
        this.cd.markForCheck();
    }

    onMonthHover(year: number, month: number): void {
        if (this.pendingStart) {
            this.hoverMonth = { year, month };
            this.cd.markForCheck();
        }
    }

    reset(): void {
        this.pendingStart = null;
        this.hoverMonth = null;
        this.showPicker = false;
        const newFrom = this.defaultFrom;
        const newTo = this.defaultTo;
        if (newFrom) this.viewYear = newFrom.getFullYear();
        this.rangeChange.emit({ dateFrom: newFrom, dateTo: newTo });
        this.cd.markForCheck();
    }

    // ─── State checks for template ───────────────────────────────────────────

    isMonthDisabled(year: number, month: number): boolean {
        if (this.minDate) {
            const cmpMin = this.compareYM({ year, month }, { year: this.minDate.getFullYear(), month: this.minDate.getMonth() });
            if (cmpMin < 0) return true;
        }
        if (this.maxDate) {
            const cmpMax = this.compareYM({ year, month }, { year: this.maxDate.getFullYear(), month: this.maxDate.getMonth() });
            if (cmpMax > 0) return true;
        }
        return false;
    }

    isMonthRangeStart(year: number, month: number): boolean {
        if (!this.dateFrom) return false;
        const d = this.dateFrom instanceof Date ? this.dateFrom : new Date(this.dateFrom);
        return d.getFullYear() === year && d.getMonth() === month;
    }

    isMonthRangeEnd(year: number, month: number): boolean {
        if (!this.dateTo) return false;
        const d = this.dateTo instanceof Date ? this.dateTo : new Date(this.dateTo);
        return d.getFullYear() === year && d.getMonth() === month;
    }

    isMonthInRange(year: number, month: number): boolean {
        if (!this.dateFrom || !this.dateTo) return false;
        const start = this.dateFrom instanceof Date ? this.dateFrom : new Date(this.dateFrom);
        const end = this.dateTo instanceof Date ? this.dateTo : new Date(this.dateTo);
        const c = this.compareYM({ year, month }, { year: start.getFullYear(), month: start.getMonth() });
        const c2 = this.compareYM({ year, month }, { year: end.getFullYear(), month: end.getMonth() });
        return c >= 0 && c2 <= 0;
    }

    isMonthPendingStart(year: number, month: number): boolean {
        return !!this.pendingStart && this.pendingStart.year === year && this.pendingStart.month === month;
    }

    isMonthInHoverRange(year: number, month: number): boolean {
        if (!this.pendingStart || !this.hoverMonth) return false;
        const lo = this.compareYM(this.pendingStart, this.hoverMonth) <= 0 ? this.pendingStart : this.hoverMonth;
        const hi = lo === this.pendingStart ? this.hoverMonth : this.pendingStart;
        const c = this.compareYM({ year, month }, lo);
        const c2 = this.compareYM({ year, month }, hi);
        return c >= 0 && c2 <= 0;
    }

    // ─── Internal ────────────────────────────────────────────────────────────

    @HostListener('document:click')
    onDocumentClick(): void {
        if (this.showPicker) {
            this.showPicker = false;
            this.pendingStart = null;
            this.hoverMonth = null;
            this.cd.markForCheck();
        }
    }

    private compareYM(a: YearMonth, b: YearMonth): number {
        if (a.year !== b.year) return a.year - b.year;
        return a.month - b.month;
    }

    private formatMonth(d: Date | string): string {
        const date = d instanceof Date ? d : new Date(d);
        return `${this.monthLabels[date.getMonth()]} ${String(date.getFullYear()).slice(-2)}`;
    }
}
